import * as React from 'react';
import { Skeleton } from '@chess/api-skeleton';
import { FilterAnchor, PractiseRows, ResultSplitRows, TextSkeleton, type CardProps, type FilterLink, type PractiseRow } from '@chess/api-insights/charts';
import { CardAside, CardEmpty, CardNote } from '@chess/api-dashboard/react';
import type { GameOutcome, GroundCause } from '@chess/api-model';
import { groundCauseView } from '@chess/api-mistakes';
import '@chess/api-ending-case/colors.css';
import { aheadOf, causeRowsOf, notWonOf, type MiddlegameConversionResult } from './MiddlegameConversionAggregator.ts';
import { CONVERSION_HELP } from './help.tsx';
import './middlegame-conversion.css';

// Converting an advantage (2026-10-10, the Middlegame screen's `middlegame.why`):
// the period's games where the reader stood winning in the middlegame (the
// Winning setting, before one of their middlegame moves), as one stacked bar
// won / drawn / lost with its three counts in the results' colours (no
// legend), then for those not won where the lead went: the largest causes
// (the Moves to practise causes), at most `MAX_CAUSE_ROWS` rows, in games;
// "no single mistake" is never a row, the help explains it (2026-10-10: the
// card made shorter).
// Every segment and row opens exactly those games in the library
// (`conversion`, with `result` on a segment). The Ending section's "Your
// losses" is its mirror for the ending. Drawn from props only: the card's
// body, which the section draws in the dashboard's frame
// (`MIDDLEGAME_CONVERSION_CARD`, `registerCard`).

export type MiddlegameConversionCardProps = CardProps<{ result: MiddlegameConversionResult }, {
  /** Opens the library filtered to a mark's games. */
  link?: FilterLink;
}>;

/** The frame's title and help topic: the registration's and the stories'. */
export const MIDDLEGAME_CONVERSION_CARD = { title: 'Converting an advantage', help: CONVERSION_HELP };
const ASIDE = 'ahead in the middlegame';
const LABEL: Record<GroundCause, string> = { tactic: 'Tactics', missed: 'Missed chances', time: 'Time trouble', positional: 'Positional' };
const DESCRIPTION: Record<GroundCause, string> = {
  tactic: groundCauseView('tactic').description,
  missed: groundCauseView('missed').description,
  time: `${groundCauseView('time').description}, or lost on time`,
  positional: groundCauseView('positional').description,
};
const OUTCOMES: ReadonlyArray<{ outcome: GameOutcome; label: string }> = [
  { outcome: 'win', label: 'Won' },
  { outcome: 'draw', label: 'Drawn' },
  { outcome: 'loss', label: 'Lost' },
];
const games = (n: number) => `${n} ${n === 1 ? 'game' : 'games'}`;

/** The bar's three counts, won–drawn–lost, each in its result's colour and opening its games. */
const Counts: React.FC<{ result: MiddlegameConversionResult; link?: FilterLink }> = ({ result, link }) => {
  const ahead = aheadOf(result);
  return (
    <span className="middlegame-conversion-counts">
      {OUTCOMES.map(({ outcome, label }, i) => (
        <React.Fragment key={outcome}>
          {i > 0 && '–'}
          <FilterAnchor
            link={result.ahead[outcome] ? link : undefined}
            filter={{ conversion: 'ahead', outcome }}
            style={{ color: `var(--viz-${outcome})` }}
            title={`${label}: ${result.ahead[outcome]} of ${games(ahead)} where you stood winning`}>
            {result.ahead[outcome]}
          </FilterAnchor>
        </React.Fragment>
      ))}
    </span>
  );
};

/** The bar and the causes; also the loading skeleton's shape, with `SAMPLE`. */
const Body: React.FC<{ result: MiddlegameConversionResult; link?: FilterLink }> = ({ result, link }) => {
  const ahead = aheadOf(result);
  const rows: PractiseRow[] = causeRowsOf(result).map(({ cause, count }) => ({
    key: cause,
    label: LABEL[cause],
    count,
    colour: groundCauseView(cause).colour,
    title: `Where the lead went: ${DESCRIPTION[cause]} · ${games(count)} · opens them`,
    filter: { conversion: cause },
  }));
  return (
    <div className="middlegame-conversion-body">
      <ResultSplitRows
        link={link}
        legend={false}
        rows={[{ key: 'ahead', label: 'Ahead', title: `${games(ahead)} where you stood winning in the middlegame`, results: result.ahead, filter: { conversion: 'ahead' }, extra: <Counts result={result} link={link} /> }]}
      />
      {rows.length ? <PractiseRows rows={rows} link={link} /> : !notWonOf(result) && <CardEmpty>You won every one.</CardEmpty>}
    </div>
  );
};

const SAMPLE: MiddlegameConversionResult = { ahead: { win: 12, draw: 2, loss: 6 }, byCause: { tactic: 4, missed: 1, time: 2, positional: 1, none: 0 } };

export const MiddlegameConversionCard: React.FC<MiddlegameConversionCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <Skeleton loading shape={<Body result={SAMPLE} />} label="Loading how your leads ended" />
        <CardNote>
          <TextSkeleton sample="12 games ahead · a mark opens its games" />
        </CardNote>
      </>
    );
  const { result, link } = props;
  const ahead = aheadOf(result);
  if (!ahead)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardEmpty>You were not ahead in a middlegame in the last 30 days.</CardEmpty>
      </>
    );
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      <Body result={result} link={link} />
      <CardNote>{`${games(ahead)} ahead · a mark opens its games`}</CardNote>
    </>
  );
};
